import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './Alerts.css';

function Alerts({ currentUser }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    type: '',
    is_read: 'false', // Default to unread only
    severity: '',
    search: '',
    date_from: '',
    date_to: ''
  });
  const [groupBy, setGroupBy] = useState('');
  const [selectedAlerts, setSelectedAlerts] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [commentsOpen, setCommentsOpen] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchAlerts();
    fetchStats();
  }, [page, filter, groupBy]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50'
      });
      
      if (filter.type) params.append('type', filter.type);
      if (filter.is_read) params.append('is_read', filter.is_read);
      if (filter.severity) params.append('severity', filter.severity);
      if (filter.search) params.append('search', filter.search);
      if (filter.date_from) params.append('date_from', filter.date_from);
      if (filter.date_to) params.append('date_to', filter.date_to);
      if (groupBy) params.append('group_by', groupBy);
      
      const response = await api.get(`/api/alerts?${params.toString()}`);
      setAlerts(response.data.alerts || []);
      setTotalPages(response.data.total_pages || 1);
      setSelectedAlerts(new Set()); // Clear selection when filters change
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/alerts/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching alert stats:', error);
    }
  };

  const fetchComments = async (alertId) => {
    try {
      const response = await api.get(`/api/alerts/${alertId}/comments`);
      setComments(prev => ({ ...prev, [alertId]: response.data.comments || [] }));
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const toggleComments = async (alertId) => {
    const isOpen = commentsOpen[alertId];
    setCommentsOpen(prev => ({ ...prev, [alertId]: !isOpen }));
    
    if (!isOpen && !comments[alertId]) {
      await fetchComments(alertId);
    }
  };

  const addComment = async (alertId) => {
    const commentText = newComment[alertId]?.trim();
    if (!commentText) return;

    try {
      const response = await api.post(`/api/alerts/${alertId}/comments`, {
        comment: commentText
      });
      
      setComments(prev => ({
        ...prev,
        [alertId]: [...(prev[alertId] || []), response.data.comment]
      }));
      setNewComment(prev => ({ ...prev, [alertId]: '' }));
      
      // Refresh alerts to update comment count
      fetchAlerts();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment: ' + (error.response?.data?.error || error.message));
    }
  };

  const markAsRead = async (alertId) => {
    try {
      await api.post(`/api/alerts/${alertId}/read`);
      setAlerts(alerts.map(alert => 
        alert.id === alertId ? { ...alert, is_read: true } : alert
      ));
      fetchStats();
      window.dispatchEvent(new Event('alertsUpdated'));
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  };

  const markAsUnread = async (alertId) => {
    try {
      await api.post(`/api/alerts/${alertId}/unread`);
      setAlerts(alerts.map(alert => 
        alert.id === alertId ? { ...alert, is_read: false } : alert
      ));
      fetchStats();
      window.dispatchEvent(new Event('alertsUpdated'));
    } catch (error) {
      console.error('Error marking alert as unread:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/api/alerts/read-all');
      setAlerts(alerts.map(alert => ({ ...alert, is_read: true })));
      fetchStats();
      window.dispatchEvent(new Event('alertsUpdated'));
    } catch (error) {
      console.error('Error marking all alerts as read:', error);
    }
  };

  const bulkMarkAsRead = async () => {
    if (selectedAlerts.size === 0) return;
    
    try {
      await api.post('/api/alerts/bulk-read', {
        alert_ids: Array.from(selectedAlerts)
      });
      
      setAlerts(alerts.map(alert => 
        selectedAlerts.has(alert.id) ? { ...alert, is_read: true } : alert
      ));
      setSelectedAlerts(new Set());
      fetchStats();
      window.dispatchEvent(new Event('alertsUpdated'));
    } catch (error) {
      console.error('Error bulk marking alerts as read:', error);
      alert('Failed to mark alerts as read: ' + (error.response?.data?.error || error.message));
    }
  };

  const bulkMarkAsUnread = async () => {
    if (selectedAlerts.size === 0) return;
    
    try {
      await api.post('/api/alerts/bulk-unread', {
        alert_ids: Array.from(selectedAlerts)
      });
      
      setAlerts(alerts.map(alert => 
        selectedAlerts.has(alert.id) ? { ...alert, is_read: false } : alert
      ));
      setSelectedAlerts(new Set());
      fetchStats();
      window.dispatchEvent(new Event('alertsUpdated'));
    } catch (error) {
      console.error('Error bulk marking alerts as unread:', error);
      alert('Failed to mark alerts as unread: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleSelectAlert = (alertId) => {
    setSelectedAlerts(prev => {
      const next = new Set(prev);
      if (next.has(alertId)) {
        next.delete(alertId);
      } else {
        next.add(alertId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAlerts.size === alerts.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(alerts.map(a => a.id)));
    }
  };

  const exportAlerts = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.type) params.append('type', filter.type);
      if (filter.is_read) params.append('is_read', filter.is_read);
      if (filter.severity) params.append('severity', filter.severity);
      if (filter.search) params.append('search', filter.search);
      if (filter.date_from) params.append('date_from', filter.date_from);
      if (filter.date_to) params.append('date_to', filter.date_to);
      
      const API_BASE = process.env.REACT_APP_API_BASE || '';
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers = {};
      if (user && user.token) {
        headers['Authorization'] = `Bearer ${user.token}`;
      }

      const response = await fetch(`${API_BASE}/api/alerts/export?${params.toString()}`, {
        headers
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `alerts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting alerts:', error);
      alert('Failed to export alerts: ' + (error.message || 'Unknown error'));
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'critical':
        return 'severity-critical';
      case 'high':
        return 'severity-high';
      case 'medium':
        return 'severity-medium';
      default:
        return 'severity-medium';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAlertClick = (alert) => {
    if (alert.dog_name) {
      navigate(`/staff/dogs?dog=${encodeURIComponent(alert.dog_name)}`);
    }
  };

  // Group alerts by dog name if groupBy is set
  const groupedAlerts = groupBy === 'dog_name' 
    ? alerts.reduce((acc, alert) => {
        const key = alert.dog_name || 'Unknown';
        if (!acc[key]) acc[key] = [];
        acc[key].push(alert);
        return acc;
      }, {})
    : null;

  const unreadCount = alerts.filter(a => !a.is_read).length;
  const allSelected = alerts.length > 0 && selectedAlerts.size === alerts.length;

  return (
    <div className="alerts-container">
      <div className="alerts-header">
        <div>
          <h2>Daily Reports & Alerts</h2>
          <p>View alerts for high-risk dogs and newly discovered dogs</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button 
            className="export-button"
            onClick={exportAlerts}
            title="Export filtered alerts to CSV"
          >
            📥 Export
          </button>
          {unreadCount > 0 && (
            <button 
              className="mark-all-read-button"
              onClick={markAllAsRead}
            >
              Mark All as Read ({unreadCount})
            </button>
          )}
        </div>
      </div>

      {stats && (
        <div className="alerts-stats">
          <div className="stat-card">
            <div className="stat-label">Total Alerts</div>
            <div className="stat-value">{stats.total || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Unread</div>
            <div className="stat-value unread">{stats.unread || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">High Risk</div>
            <div className="stat-value">{stats.high_risk_count || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">New Dogs</div>
            <div className="stat-value">{stats.new_dog_count || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Recently Adopted</div>
            <div className="stat-value" style={{ color: '#16a34a' }}>{stats.recently_adopted_count || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Critical</div>
            <div className="stat-value critical">{stats.critical_count || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Today</div>
            <div className="stat-value">{stats.today_count || 0}</div>
          </div>
        </div>
      )}

      <div className="alerts-filters">
        <input
          type="text"
          placeholder="Search alerts..."
          value={filter.search}
          onChange={(e) => {
            setFilter({ ...filter, search: e.target.value });
            setPage(1);
          }}
          className="search-input"
        />

        <input
          type="date"
          placeholder="From date"
          value={filter.date_from}
          onChange={(e) => {
            setFilter({ ...filter, date_from: e.target.value });
            setPage(1);
          }}
          className="date-input"
        />

        <input
          type="date"
          placeholder="To date"
          value={filter.date_to}
          onChange={(e) => {
            setFilter({ ...filter, date_to: e.target.value });
            setPage(1);
          }}
          className="date-input"
        />

        <select
          value={filter.type}
          onChange={(e) => {
            setFilter({ ...filter, type: e.target.value });
            setPage(1);
          }}
        >
          <option value="">All Types</option>
          <option value="high_risk_score">High Risk Score</option>
          <option value="new_dog">New Dog</option>
          <option value="recently_adopted">Recently Adopted</option>
        </select>

        <select
          value={filter.is_read}
          onChange={(e) => {
            setFilter({ ...filter, is_read: e.target.value });
            setPage(1);
          }}
        >
          <option value="">All</option>
          <option value="false">Unread Only</option>
          <option value="true">Read Only</option>
        </select>

        <select
          value={filter.severity}
          onChange={(e) => {
            setFilter({ ...filter, severity: e.target.value });
            setPage(1);
          }}
        >
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
        </select>

        <select
          value={groupBy}
          onChange={(e) => {
            setGroupBy(e.target.value);
            setPage(1);
          }}
        >
          <option value="">No Grouping</option>
          <option value="dog_name">Group by Dog</option>
        </select>
      </div>

      {selectedAlerts.size > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedAlerts.size} alert(s) selected</span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={bulkMarkAsRead}>Mark as Read</button>
            <button onClick={bulkMarkAsUnread}>Mark as Unread</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading alerts...</div>
      ) : alerts.length === 0 ? (
        <div className="no-alerts">
          <p>No alerts found matching your filters.</p>
        </div>
      ) : (
        <>
          {groupedAlerts ? (
            <div className="alerts-list">
              {Object.entries(groupedAlerts).map(([dogName, groupAlerts]) => {
                const isExpanded = expandedGroups.has(dogName);
                return (
                  <div key={dogName} className="alert-group">
                    <div 
                      className="alert-group-header"
                      onClick={() => {
                        setExpandedGroups(prev => {
                          const next = new Set(prev);
                          if (next.has(dogName)) {
                            next.delete(dogName);
                          } else {
                            next.add(dogName);
                          }
                          return next;
                        });
                      }}
                    >
                      <span>{dogName || 'Unknown'} ({groupAlerts.length})</span>
                      <span>{isExpanded ? '▼' : '▶'}</span>
                    </div>
                    {isExpanded && (
                      <div className="alert-group-content">
                        {groupAlerts.map((alert) => (
                          <AlertItem
                            key={alert.id}
                            alert={alert}
                            selectedAlerts={selectedAlerts}
                            toggleSelect={toggleSelectAlert}
                            markAsRead={markAsRead}
                            markAsUnread={markAsUnread}
                            handleAlertClick={handleAlertClick}
                            getSeverityClass={getSeverityClass}
                            formatDate={formatDate}
                            commentsOpen={commentsOpen}
                            toggleComments={toggleComments}
                            comments={comments}
                            newComment={newComment}
                            setNewComment={setNewComment}
                            addComment={addComment}
                            currentUser={currentUser}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="alerts-list">
              <div className="select-all-bar">
                <label>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                  Select All
                </label>
              </div>
              {alerts.map((alert) => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  selectedAlerts={selectedAlerts}
                  toggleSelect={toggleSelectAlert}
                  markAsRead={markAsRead}
                  markAsUnread={markAsUnread}
                  handleAlertClick={handleAlertClick}
                  getSeverityClass={getSeverityClass}
                  formatDate={formatDate}
                  commentsOpen={commentsOpen}
                  toggleComments={toggleComments}
                  comments={comments}
                  newComment={newComment}
                  setNewComment={setNewComment}
                  addComment={addComment}
                  currentUser={currentUser}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AlertItem({ 
  alert, 
  selectedAlerts, 
  toggleSelect, 
  markAsRead, 
  markAsUnread, 
  handleAlertClick,
  getSeverityClass,
  formatDate,
  commentsOpen,
  toggleComments,
  comments,
  newComment,
  setNewComment,
  addComment,
  currentUser
}) {
  const isSelected = selectedAlerts.has(alert.id);
  const isCommentsOpen = commentsOpen[alert.id];
  const alertComments = comments[alert.id] || [];

  return (
    <div
      className={`alert-item ${alert.is_read ? 'read' : 'unread'} ${getSeverityClass(alert.severity)}`}
    >
      <div className="alert-checkbox">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleSelect(alert.id)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="alert-content">
        <div className="alert-header">
          <div className="alert-type-badge">
            {alert.alert_type === 'high_risk_score' ? '⚠️ High Risk' : 
             alert.alert_type === 'recently_adopted' ? '🎉 Recently Adopted' : 
             '🆕 New Dog'}
          </div>
          <div className={`severity-badge ${getSeverityClass(alert.severity)}`}>
            {alert.severity}
          </div>
          {!alert.is_read && <span className="unread-indicator">●</span>}
        </div>
        <div className="alert-message">{alert.message}</div>
        {alert.dog_name && (
          <div className="alert-dog">
            <strong>Dog:</strong> {alert.dog_name}
          </div>
        )}
        {alert.metadata && (
          <div className="alert-metadata">
            {alert.metadata.risk_score && (
              <span>Risk Score: {alert.metadata.risk_score}</span>
            )}
            {alert.metadata.days_ago !== undefined && (
              <span>First race: {alert.metadata.days_ago} day(s) ago</span>
            )}
          </div>
        )}
        <div className="alert-footer" onClick={(e) => e.stopPropagation()}>
          <span className="alert-time">{formatDate(alert.created_at)}</span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="comments-button"
              onClick={() => toggleComments(alert.id)}
            >
              💬 Comments ({alert.comment_count || 0})
            </button>
            {alert.dog_name && (
              <button
                className="view-dog-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAlertClick(alert);
                }}
              >
                View Dog
              </button>
            )}
            {!alert.is_read ? (
              <button
                className="mark-read-button"
                onClick={() => markAsRead(alert.id)}
              >
                Mark as Read
              </button>
            ) : (
              <button
                className="mark-unread-button"
                onClick={() => markAsUnread(alert.id)}
              >
                Mark as Unread
              </button>
            )}
          </div>
        </div>
        
        {isCommentsOpen && (
          <div className="alert-comments-section">
            <div className="comments-list">
              {alertComments.length === 0 ? (
                <p className="no-comments">No comments yet</p>
              ) : (
                alertComments.map(comment => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <strong>{comment.user?.username || 'Unknown'}</strong>
                      <span className="comment-time">
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                    <div className="comment-text">{comment.comment}</div>
                  </div>
                ))
              )}
            </div>
            <div className="add-comment">
              <textarea
                placeholder="Add a comment..."
                value={newComment[alert.id] || ''}
                onChange={(e) => setNewComment(prev => ({ ...prev, [alert.id]: e.target.value }))}
                rows={2}
              />
              <button onClick={() => addComment(alert.id)}>Add Comment</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Alerts;
